<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { formatNumber } from '@chatwoot/utils';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';
import { READ_STATUS } from 'dashboard/constants/gch';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import ConversationFilterPanel from './widgets/conversation/ConversationFilterPanel.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  activeReadStatus: { type: String, required: true },
  activeLabels: { type: Array, required: true },
  filterLabelGroups: { type: Array, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'applyPanelFilters',
]);

const [showFilterPanel, toggleFilterPanel] = useToggle();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const hasReadStatusFilter = computed(
  () => props.activeReadStatus !== READ_STATUS.ALL
);

const hasPanelFilters = computed(
  () =>
    hasReadStatusFilter.value ||
    props.activeLabels.length > 0 ||
    props.activeStatus !== wootConstants.STATUS_TYPE.OPEN
);

const toggleSidebar = () => emitter.emit(BUS_EVENTS.TOGGLE_SIDEBAR);
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 h-[3.25rem]"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <h1
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders && hasReadStatusFilter"
        class="px-2 py-1 my-0.5 rounded-md bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.FILTER_PANEL.READ_STATUS.${activeReadStatus}`) }}
      </span>
    </div>
    <div class="flex items-center gap-1">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div
        v-else
        v-on-click-outside="() => toggleFilterPanel(false)"
        class="relative"
      >
        <NextButton
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          :color="hasPanelFilters ? 'blue' : 'slate'"
          xs
          faded
          @click="toggleFilterPanel()"
        />
        <ConversationFilterPanel
          v-if="showFilterPanel"
          class="absolute z-50 mt-2 top-full ltr:right-0 rtl:left-0"
          :status="activeStatus"
          :read-status="activeReadStatus"
          :labels="activeLabels"
          :label-groups="filterLabelGroups"
          @apply="emit('applyPanelFilters', $event)"
          @close="toggleFilterPanel(false)"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <NextButton
        v-tooltip.left="$t('SIDEBAR.HIDE_MENU')"
        icon="i-lucide-arrow-left-to-line"
        slate
        xs
        faded
        class="flex-shrink-0 rtl:rotate-180 md:inline-flex hidden"
        @click="toggleSidebar"
      />
    </div>
  </div>
</template>
